import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { BookmarkFolder } from '@ext-bookmark/shared/types';

interface BookmarkSidebarProps {
  folders: BookmarkFolder[];
  selectedFolderId: string | null;
  onAddFolder: (parentId?: string | null) => void;
  onRenameFolder?: (folder: BookmarkFolder) => void;
  onDeleteFolder?: (folder: BookmarkFolder) => void;
}

function buildTree(folders: BookmarkFolder[], parentId: string | null): BookmarkFolder[] {
  return folders
    .filter((f) => f.parent_id === parentId)
    .sort((a, b) => a.position - b.position || (a.created_at < b.created_at ? -1 : 1));
}

const FolderNode: React.FC<{
  folder: BookmarkFolder;
  all: BookmarkFolder[];
  selectedId: string | null;
  level: number;
  onAddFolder: (parentId?: string | null) => void;
  onRenameFolder?: (folder: BookmarkFolder) => void;
  onDeleteFolder?: (folder: BookmarkFolder) => void;
}> = ({ folder, all, selectedId, level, onAddFolder, onRenameFolder, onDeleteFolder }) => {
  const [expanded, setExpanded] = useState(true);
  const children = buildTree(all, folder.id);
  const isSelected = selectedId === folder.id;
  const hasChildren = children.length > 0;

  return (
    <div className="flex flex-col">
      <div
        className="flex items-center gap-1 py-1.5 px-2 rounded-lg cursor-pointer transition-colors min-h-[44px] min-w-[44px] touch-manipulation group"
        style={{ paddingLeft: `${8 + level * 12}px` }}
      >
        <button
          type="button"
          className="flex-shrink-0 p-0.5 rounded hover:bg-black/10 dark:hover:bg-white/10"
          onClick={() => setExpanded((e) => !e)}
          aria-label={expanded ? '折叠' : '展开'}
        >
          {hasChildren ? (
            <svg
              className={`w-4 h-4 transition-transform ${expanded ? '' : '-rotate-90'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          ) : (
            <span className="w-4 inline-block" />
          )}
        </button>
        <Link
          to={folder.id === selectedId ? '/bookmarks' : `/bookmarks/${folder.id}`}
          className={`flex-1 min-w-0 truncate text-sm ${isSelected ? 'font-semibold text-primary' : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'}`}
        >
          {folder.name}
        </Link>
      </div>
      {expanded &&
        children.map((child) => (
          <FolderNode
            key={child.id}
            folder={child}
            all={all}
            selectedId={selectedId}
            level={level + 1}
            onAddFolder={onAddFolder}
            onRenameFolder={onRenameFolder}
            onDeleteFolder={onDeleteFolder}
          />
        ))}
    </div>
  );
};

const BookmarkSidebar: React.FC<BookmarkSidebarProps> = ({
  folders,
  selectedFolderId,
  onAddFolder,
  onRenameFolder,
  onDeleteFolder,
}) => {
  const roots = buildTree(folders, null);

  return (
    <aside className="w-56 flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex flex-col overflow-hidden">
      <div className="p-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">书签栏</span>
        <button
          type="button"
          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400 min-h-[44px] min-w-[44px] touch-manipulation"
          onClick={() => onAddFolder(null)}
          title="新建文件夹"
          aria-label="新建文件夹"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>
      <div className="flex-1 overflow-y-auto py-2">
        {roots.length === 0 ? (
          <div className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
            暂无文件夹，点击上方 + 创建
          </div>
        ) : (
          roots.map((f) => (
            <FolderNode
              key={f.id}
              folder={f}
              all={folders}
              selectedId={selectedFolderId}
              level={0}
              onAddFolder={onAddFolder}
              onRenameFolder={onRenameFolder}
              onDeleteFolder={onDeleteFolder}
            />
          ))
        )}
      </div>
    </aside>
  );
};

export default BookmarkSidebar;
